"use client";

import { useEffect, useState, useCallback, useSyncExternalStore } from "react";

/** Hydration-safe media state for header markup that is rendered on the server. */
export function useResponsiveWidth(width: number) {
  const query = `(min-width: ${width}px)`;
  const subscribe = useCallback(
    (notify: () => void) => {
      const media = window.matchMedia(query);
      media.addEventListener("change", notify);
      return () => media.removeEventListener("change", notify);
    },
    [query],
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => true,
  );
}

export const navigationBreakpoints = { md: 768, lg: 1024, xl: 1200 } as const;
export type NavigationBreakpoint = keyof typeof navigationBreakpoints;

/** Only for components that mount on the client after interaction; the first value is read synchronously. */
export function useMinWidth(width: number) {
  const query = `(min-width: ${width}px)`;
  const [matches, setMatches] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);
  return matches;
}
